import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IDataType, IItemOperationSetup, ISortSetting, ITerminalTemplate, TerminalTemplateAdministrationService } from '@shared/services/terminaltemplateAdministration.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-edit-terminaltemplate',
	templateUrl: './edit-terminaltemplate.component.html',
	styleUrls: ['./edit-terminaltemplate.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EditTerminalTemplateComponent implements OnInit {
	@Output() edited: EventEmitter<any> = new EventEmitter();

	form: FormGroup;
	loading: boolean = false;
	terminalTemplate: ITerminalTemplate;
	httpError: string = '';
	dataTypes: string[] = Object.keys(IDataType).filter((key) => Number.isNaN(Number(IDataType[key])));

	sortSettings: string[] = Object.keys(ISortSetting).filter((key) => Number.isNaN(Number(ISortSetting[key])));

	constructor(
		@Inject(MAT_DIALOG_DATA) public editTerminalTemplate: ITerminalTemplate,
		public dialogRef: MatDialogRef<EditTerminalTemplateComponent>,
		private terminalTemplateService: TerminalTemplateAdministrationService,
		private fb: FormBuilder,
	) {
		this.terminalTemplate = JSON.parse(JSON.stringify(editTerminalTemplate));
	}

	ngOnInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.terminalTemplate.alias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
			name: [this.terminalTemplate.name, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			groupList: this.fb.array([]),
			unitList: this.fb.array([]),
			detailView: [this.terminalTemplate.data.detailView, [Validators.maxLength(200000)]],
		});

		this.populateFormArrays();

		this.form.valueChanges.subscribe((value) => {
			// Loop over all top-level keys in the form's value
			Object.keys(value).forEach((key) => {
				// If it's detailView, groupList, or unitList, store it under this.terminalTemplate.data
				if (key === 'groupList' || key === 'unitList' || key === 'detailView') {
					this.terminalTemplate.data[key] = value[key];
				}
				// Else, if the key already exists on this.terminalTemplate, assign it there
				else if (key in this.terminalTemplate) {
					this.terminalTemplate[key] = value[key];
				}
			});
		});
	}

	populateFormArrays(): void {
		if (this.terminalTemplate.data.groupList) {
			this.terminalTemplate.data.groupList.forEach((unit) => {
				this.groupList.push(this.createListItem(unit));
			});
		}

		if (this.terminalTemplate.data.unitList) {
			this.terminalTemplate.data.unitList.forEach((unit) => {
				this.unitList.push(this.createListItem(unit));
			});
		}
	}

	get controls() {
		return this.form.controls;
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	get groupList(): FormArray<FormGroup> {
		return this.form.get('groupList') as FormArray<FormGroup>;
	}

	get unitList(): FormArray<FormGroup> {
		return this.form.get('unitList') as FormArray<FormGroup>;
	}

	addUnitListItem(): void {
		this.unitList.push(
			this.createListItem({
				source: '',
				title: '',
				dataType: IDataType.String,
				format: '',
				sort: ISortSetting.None,
			}),
		);
	}

	deleteUnitListItem(index: number): void {
		this.unitList.removeAt(index);
	}

	addGroupListItem(): void {
		this.groupList.push(
			this.createListItem({
				source: '',
				title: '',
				dataType: IDataType.String,
				format: '',
				sort: ISortSetting.None,
			}),
		);
	}

	deleteGroupListItem(index: number): void {
		this.groupList.removeAt(index);
	}

	createListItem(unit: IItemOperationSetup): FormGroup {
		return this.fb.group({
			source: [unit.source, [Validators.required, Validators.maxLength(255)]],
			title: [unit.title, [Validators.required, Validators.maxLength(255)]],
			datatype: [unit.dataType, Validators.required],
			format: [unit.format],
			sort: [unit.sort, Validators.required],
		});
	}

	onDropGroup(event: CdkDragDrop<AbstractControl[]>): void {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const movedControl = this.groupList.at(event.previousIndex);
		this.groupList.removeAt(event.previousIndex);
		this.groupList.insert(event.currentIndex, movedControl);
	}

	onDropUnit(event: CdkDragDrop<AbstractControl[]>): void {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const movedControl = this.unitList.at(event.previousIndex);
		this.unitList.removeAt(event.previousIndex);
		this.unitList.insert(event.currentIndex, movedControl);
	}

	onCancel = () => this.dialogRef.close();

	onSave() {
		if (this.loading) return;
		this.httpError = '';
		if (this.form.valid) {
			this.loading = true;

			this.terminalTemplate.data.groupList = this.groupList.value.filter((grp) => grp.title !== '');

			this.terminalTemplateService.updateTerminalTemplate(this.terminalTemplate).subscribe({
				next: () => {
					this.edited.emit();
					this.dialogRef.close();
				},
				error: (errorResponse) => {
					this.httpError = `${errorResponse.status}`;
					this.loading = false;
				},
			});
		}
	}
}
